'use client';

import { Project, ProjectMetric } from "@prisma/client"; 
import { FiGithub, FiExternalLink, FiCalendar, FiTag, FiLayers, FiActivity, FiAward, FiBookOpen } from "react-icons/fi";
import Image from "next/image";
import { motion } from "framer-motion";
import clsx from "clsx";
import MDXContent from "@/components/mdx/MDXContent"; // We'll build this next

interface ProjectDetailClientProps {
  project: Project & { metrics: ProjectMetric[] };
  relatedProjects: any[];
  mdxSource?: any; // The serialized MDX from the server
}

export default function ProjectDetailClient({ project, relatedProjects, mdxSource }: ProjectDetailClientProps) {
  return (
    <main className="min-h-screen bg-white dark:bg-zinc-950 pt-24 pb-20">
      
      {/* 1. Media Theater */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 mb-16">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative aspect-video w-full overflow-hidden rounded-[2.5rem] bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl"
        >
          {project.videoUrl ? (
            <iframe src={project.videoUrl} className="w-full h-full" allowFullScreen />
          ) : (
            <Image
              src={project.coverImage || '/images/placeholder.jpg'}
              alt={project.title}
              fill
              className="object-cover opacity-90"
              priority
            />
          )}
          
          <div className="absolute top-6 left-6 flex items-center gap-2 px-3 py-1.5 bg-black/50 backdrop-blur-md rounded-full border border-white/10">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-widest text-white">Project_Session: Active</span>
          </div>
        </motion.div>
      </section>

      {/* 2. Content Architecture */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 grid grid-cols-1 lg:grid-cols-3 gap-16">
        
        <div className="lg:col-span-2 space-y-20">
          <header className="space-y-6">
            <div className="flex items-center gap-3 text-indigo-500">
              <FiBookOpen size={18} />
              <span className="text-[10px] font-black uppercase tracking-[0.4em]">Case Study Analysis</span>
            </div>
            <motion.h1 className="text-6xl md:text-8xl font-black tracking-tighter text-zinc-900 dark:text-white uppercase leading-[0.9]">
              {project.title}
            </motion.h1>
            <p className="text-xl text-zinc-600 dark:text-zinc-400 leading-relaxed font-medium max-w-2xl">
              {project.description}
            </p>
          </header>

          {/* Strategic Outcomes (Metrics) */}
          {project.metrics?.length > 0 && (
            <section className="space-y-8 p-10 rounded-[3rem] bg-zinc-50 dark:bg-zinc-900/30 border border-zinc-100 dark:border-zinc-800/50">
              <div className="flex items-center gap-4">
                <h3 className="text-xs font-black uppercase tracking-[0.3em] text-indigo-500">Validation Metrics</h3>
                <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-800" />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {project.metrics.map((metric) => (
                  <div key={metric.id} className="p-6 rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 shadow-sm flex items-center gap-4">
                    <div className={clsx(
                      "p-3 rounded-xl",
                      metric.category === 'ACADEMIC' ? "bg-purple-50 text-purple-600" : "bg-emerald-50 text-emerald-600"
                    )}>
                      {metric.category === 'ACADEMIC' ? <FiAward size={20} /> : <FiActivity size={20} />}
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400">{metric.label}</p>
                      <p className="text-lg font-black text-zinc-900 dark:text-white">
                        {metric.displayValue || `${metric.value}${metric.unit || ''}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 3. The Narrative Content (MDX Render) */}
          {mdxSource && (
            <section className="prose prose-zinc dark:prose-invert max-w-none">
              <div className="flex items-center gap-4 mb-12">
                <h3 className="text-xs font-black uppercase tracking-[0.3em] text-zinc-400">Detailed Intelligence</h3>
                <div className="h-px flex-1 bg-zinc-100 dark:border-zinc-800" />
              </div>
              <MDXContent source={mdxSource} />
            </section>
          )}

          {/* Gallery - Displayed only if no MDX or specifically added */}
          {!mdxSource && project.gallery.length > 0 && (
             <section className="space-y-8">
               <div className="flex items-center gap-4">
                 <h3 className="text-xs font-black uppercase tracking-[0.3em] text-zinc-400">Visual Evidence</h3>
                 <div className="h-px flex-1 bg-zinc-100 dark:bg-zinc-800" />
               </div>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 {project.gallery.map((img, i) => (
                   <div key={i} className="relative aspect-[4/3] rounded-[2rem] overflow-hidden border border-zinc-100 dark:border-zinc-800">
                     <Image src={img} alt={`Gallery ${i}`} fill className="object-cover" />
                   </div>
                 ))}
               </div>
             </section>
          )}
        </div>

        {/* Sidebar */}
        <aside className="relative">
          <div className="sticky top-32 space-y-8 p-8 rounded-[2.5rem] bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-100 dark:border-zinc-800 shadow-sm">
            
            <div className="space-y-3">
              {project.liveDemo && (
                <a href={project.liveDemo} target="_blank" className="flex items-center justify-center gap-2 w-full py-4 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-500/20">
                  Execute Live <FiExternalLink />
                </a>
              )}
              {project.repository && (
                <a href={project.repository} target="_blank" className="flex items-center justify-center gap-2 w-full py-4 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl font-black text-xs uppercase tracking-widest transition-all">
                  Repository <FiGithub />
                </a>
              )}
            </div>

            <hr className="border-zinc-200 dark:border-zinc-800" />

            <div className="space-y-6">
              <SpecBox icon={<FiLayers />} label="Project Class" value={project.category || "General"} />
              
              {project.publishedAt && (
                <SpecBox icon={<FiCalendar />} label="Deployment" value={new Date(project.publishedAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} />
              )}
              
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-zinc-400">
                  <FiTag size={14} />
                  <span className="text-[10px] font-black uppercase tracking-[0.3em]">Core Tech</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {project.techStack.map(tech => (
                    <span key={tech} className="px-3 py-1.5 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl text-[10px] font-bold text-zinc-600 dark:text-zinc-300 uppercase">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}

function SpecBox({ icon, label, value }: { icon: any, label: string, value: string }) {
  return (
    <div className="flex items-center gap-4 group">
      <div className="p-3 rounded-xl bg-white dark:bg-zinc-800 text-indigo-500 border border-zinc-200 dark:border-zinc-700">
        {icon}
      </div>
      <div>
        <p className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-400">{label}</p>
        <p className="text-sm font-bold text-zinc-900 dark:text-zinc-100 leading-tight">{value}</p>
      </div>
    </div>
  );
}